<template>
    <div class="weryai-image-2-model-page relative xl:pt-[6rem] pt-[3rem]">
        <video class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] object-cover z-0 pointer-events-none"
            :poster="heroPoster" src="https://static.weryai.com/cms/weryai_image2_show_video.mp4" autoplay muted loop
            preload="metadata" playsinline webkit-playsinline>
            <track kind="captions" />
        </video>
        <div class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] bg-black/20 pointer-events-none z-[1]"></div>
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section relative z-11">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <div class="page-name animate-float backdrop-blur-md">
                                <span class="light-dot"></span>
                                <span>WeryAI Image 2.0 ·
                                    {{
                                        $t('weryAI-image-2.officialLaunch')
                                    }}</span>
                            </div>
                            <h1>{{ $t('weryAI-image-2.generatorTitle') }}</h1>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <!-- PC 端显示 NewImageGenerator -->
                    <div class="hidden md:block">
                        <NewImageGenerator model-name="WERYAI_IMAGE_2_0" redirect-url="/ai-video"
                            @generate="handleGenerate" model-key="WERYAI_IMAGE_2_0" default-tab="image"
                            :require-prompt-for-image="true">
                            <template #model-logo>
                                <div class="openai-logo">O</div>
                            </template>
                        </NewImageGenerator>
                    </div>
                    <!-- 移动端显示 MobileGenerator -->
                    <div class="block md:hidden">
                        <MobileGenerator type="image" model-name="WeryAI Image 2.0" model-key="WERYAI_IMAGE_2_0"
                            default-tab="image" :require-prompt-for-image="true" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section relative z-10 !bg-transparent">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title mt-[2rem]">
                        {{ $t('weryAI-image-2.coreFeaturesTitle') }}
                    </h2>
                </div>
                <div class="features-showcase-container grid grid-cols-1 xl:grid-cols-2 gap-[80px]">
                    <div v-for="feature in coreFeatures" :key="feature.title" class="feature-card">
                        <div class="flex flex-col">
                            <div class="feature-video">
                                <img class="rounded-[20px]" :src="feature.url" :alt="feature.title" />
                            </div>
                            <div class="feature-text !px-0">
                                <h3 class="feature-title">
                                    {{ feature.title }}
                                </h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ $t('weryAI-image-2.howToUseTitle') }}
                    </h2>
                    <h3 class="text-text-adad text-base">
                        {{ $t('weryAI-image-2.howToUseSubtitle') }}
                    </h3>
                </div>
                <ol class="gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    <li v-for="step in howToUseStepsList" :key="step.title"
                        class="step-card flex flex-col items-center justify-center gap-5 min-h-[150px] md:min-h-[200px] lg:min-h-[270px] border border-white/10 rounded-[20px] p-5">
                        <img :src="step.icon" :alt="step.title" class="step-icon w-[96px] h-[96px] object-contain" />
                        <div class="step-content">
                            <h3 class="text-[20px] font-bold text-white">
                                {{ step.title }}
                            </h3>
                            <p v-html="step.description" class="text-sm text-text-adad"></p>
                        </div>
                    </li>
                </ol>
            </div>
            <p class="generator-description !mt-6 !mx-auto !max-w-5xl px-[1rem] !text-text-adad"
                v-html="$t('weryAI-image-2.generatorDescription')"></p>
        </section>

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ $t('weryAI-image-2.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <RouteLocalizedLink to="/image/nano-banana-pro" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                alt="Nano Banana Pro" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">Nano Banana Pro</h3>
                    </RouteLocalizedLink>

                    <RouteLocalizedLink to="/image/seedream-4-5" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/F07CFB2E-40E7-435D-B82F-E760898DD950.png"
                                alt="Seedream 4.5" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">Seedream 4.5</h3>
                    </RouteLocalizedLink>

                    <RouteLocalizedLink to="/image/qwen-image" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/88fca6eaf44c1b7c1e02caa0a061fe11.png"
                                alt="Qwen Image" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">Qwen Image</h3>
                    </RouteLocalizedLink>

                    <RouteLocalizedLink to="/image/dreamina" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/F07CFB2E-40E7-435D-B82F-E760898DD950.png"
                                alt="Dreamina 4.0" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">Dreamina 4.0</h3>
                    </RouteLocalizedLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ $t('weryAI-image-2.faqTitle') }}
                    </h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- WeryAI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="WeryAI logo" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">
                            {{ $t('weryAI-image-2.ctaTitle') }}
                        </h2>
                        <p class="cta-description">
                            {{ $t('weryAI-image-2.ctaDesc') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ $t('weryAI-image-2.ctaBtn') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ $t('weryAI-image-2.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { analyticsEvent } from '~/utils/log-event-func'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useFaqSeo } from '~/composables/useFaqSeo'
import { useImageModelFaq } from '~/composables/useImageModelFaq'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import { useI18n } from '~/composables/useI18n'
import { getResourceThumbnail } from '~/utils/common'
import MobileGenerator from '~/components/mobile/MobileGenerator.vue'
import { useSettingVideoSEO } from '~/composables/useSettingVideoSEO'

const { t } = useI18n()

// 移动端检测
const isMobile = ref(false)
const MOBILE_BREAKPOINT = 768

const checkIsMobile = () => {
    if (import.meta.client) {
        isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
    }
}

// 防抖处理 resize 事件
let resizeTimer: ReturnType<typeof setTimeout> | null = null
const handleResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
        checkIsMobile()
    }, 150)
}

const displayName = 'WeryAI Image 2.0'
const databaseName = 'weryai-image-2.0'
const modelTitle = computed(() => t('weryAI-image-2.generatorTitle'))
const modelDescription = computed(() =>
    t('weryAI-image-2.generatorDescription')
)

const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'weryai_image_2' })

const heroPoster = computed(() =>
    getResourceThumbnail(
        'https://static.weryai.com/growth/image/weryai_9551b19ffa039a17eb3a62f1c8cccc6d.webp'
    )
)

useHead({
    link: [
        {
            rel: 'preload',
            as: 'image',
            href: heroPoster.value,
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}


// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // Handle generation logic here, such as sending to API
    // 组件内部已经处理了跳转逻辑
}

// HowTo SEO - 注入结构化数据
const howToUseSteps = computed(() => [
    {
        title: t('weryAI-image-2.step1Title'),
        description: t('weryAI-image-2.step1Desc'),
    },
    {
        title: t('weryAI-image-2.step2Title'),
        description: t('weryAI-image-2.step2Desc'),
    },
    {
        title: t('weryAI-image-2.step3Title'),
        description: t('weryAI-image-2.step3Desc'),
    },
])

useHowToSeo(
    computed(() => t('weryAI-image-2.howToUseTitle')),
    howToUseSteps
)

// Page Mounted
onMounted(() => {
    // Page view analytics tracking
    analyticsEvent('page_view_weryai_image_2_0')
    analyticsEvent('view_model_page_common', {
        model_name: databaseName,
        type: 'image',
    })

    // 初始化移动端检测
    checkIsMobile()
    window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
    // 清理 resize 事件监听器
    window.removeEventListener('resize', handleResize)
    if (resizeTimer) clearTimeout(resizeTimer)
})

// FAQ Data
const faqData = useImageModelFaq('weryai_image_2')

// FAQ SEO - 注入 FAQPage 结构化数据
useFaqSeo(faqData)

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

const coreFeatures = computed(() => [
    {
        title: t('weryAI-image-2.feature1Title'),
        description: t('weryAI-image-2.feature1Desc'),
        url: 'https://static.weryai.com/cms/Frame 2147224762.webp',
    },
    {
        title: t('weryAI-image-2.feature2Title'),
        description: t('weryAI-image-2.feature2Desc'),
        url: 'https://static.weryai.com/cms/Frame 2147224764.webp',
    },
    {
        title: t('weryAI-image-2.feature3Title'),
        description: t('weryAI-image-2.feature3Desc'),
        url: 'https://static.weryai.com/cms/Frame 2147224763-instance.webp',
    },
    {
        title: t('weryAI-image-2.feature4Title'),
        description: t('weryAI-image-2.feature4Desc'),
        url: 'https://static.weryai.com/cms/Frame 2147224761.webp',
    },
])

// HowTo Steps for SEO
const howToUseStepsList = computed(() => [
    {
        title: t('weryAI-image-2.step1Title'),
        description: t('weryAI-image-2.step1Desc'),
        icon: 'https://static.weryai.com/cms/weryai_d235e92179a57a96bd38dc29336cc6bf.webp',
    },
    {
        title: t('weryAI-image-2.step2Title'),
        description: t('weryAI-image-2.step2Desc'),
        icon: 'https://static.weryai.com/cms/weryai_3585be709806892f4d0a51e8b53d4c20.webp',
    },
    {
        title: t('weryAI-image-2.step3Title'),
        description: t('weryAI-image-2.step3Desc'),
        icon: 'https://static.weryai.com/cms/weryai_e637ad7b5e3ed566325b8a9be194bfd2.webp',
    },
])

useSettingVideoSEO({
    key: 'weryaiImage2-imageobject',
    title: t('weryAI-image-2.generatorTitle'),
    description: t('weryAI-image-2.generatorDescription'),
    contentUrl: 'https://static.weryai.com/cms/weryai_image2_show_video.mp4',
    thumbnailUrl: heroPoster.value,

})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

/* 移动端样式覆盖 */
@media (max-width: 768px) {
    .container {
        padding: 0 16px !important;
    }

    .generator-container {
        padding: 0 !important;
        max-width: 100% !important;
    }

    /* 功能特性卡片样式 */
    .feature-card {
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        background: #120E11;
        padding: 16px;
        overflow: hidden;
    }

    .features-showcase-container {
        gap: 16px !important;
    }

    /* 如何使用部分 - 移动端横向布局 */
    .how-to-use-section ol {
        gap: 12px !important;
    }

    .how-to-use-section ol li.step-card {
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 16px !important;
        min-height: auto !important;
        padding: 16px !important;
        border-radius: 16px !important;
    }

    /* 第1个和第2个卡片样式 */
    .how-to-use-section ol li.step-card:nth-child(1),
    .how-to-use-section ol li.step-card:nth-child(2) {
        border: 1px solid rgba(255, 255, 255, 0.10) !important;
        background: rgba(255, 255, 255, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    /* 第3个卡片样式 */
    .how-to-use-section ol li.step-card:nth-child(3) {
        border: 1px solid rgba(255, 255, 255, 0.10) !important;
        background: rgba(153, 247, 104, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    .how-to-use-section .step-icon {
        width: 64px !important;
        height: 64px !important;
        flex-shrink: 0;
    }

    .how-to-use-section .step-content {
        flex: 1;
        text-align: left;
    }

    .how-to-use-section .step-content h3 {
        font-size: 16px !important;
        text-align: left !important;
        margin-bottom: 4px;
    }

    .how-to-use-section .step-content p {
        text-align: left !important;
        margin: 0;
    }
}
</style>
